import React, { useState, useEffect, useRef } from 'react';
import { LinkItem } from '../../types/familyLinks';

function normalizeUrl(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return 'https://' + trimmed;
}

interface LinkModalProps {
  editing: LinkItem | null;
  defaultCategory: string;
  categories: string[];
  onSave: (data: { id: string; name: string; url: string; category: string }) => void;
  onClose: () => void;
}

const LinkModal: React.FC<LinkModalProps> = ({
  editing,
  defaultCategory,
  categories,
  onSave,
  onClose,
}) => {
  const [name, setName] = useState(editing?.name || '');
  const [url, setUrl] = useState(editing?.url || '');
  const [category, setCategory] = useState(defaultCategory || categories[0] || '');
  const [error, setError] = useState('');
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Title is required'); return; }
    const normalized = normalizeUrl(url);
    if (!normalized) { setError('URL is required'); return; }
    try { new URL(normalized); } catch { setError('Enter a valid URL'); return; }
    onSave({
      id: editing?.id ?? Date.now().toString(),
      name: name.trim(),
      url: normalized,
      category,
    });
  };

  return (
    <div className="family-links-modal-overlay" onClick={onClose}>
      <div className="family-links-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="family-links-modal-title">
          {editing ? 'edit link' : 'new link'}
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="family-links-modal-field">
            <label className="family-links-modal-label" htmlFor="fl-name">Title</label>
            <input
              ref={nameRef}
              id="fl-name"
              className="family-links-modal-input"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              placeholder="e.g. PG&E Account"
            />
          </div>
          <div className="family-links-modal-field">
            <label className="family-links-modal-label" htmlFor="fl-url">Link</label>
            <input
              id="fl-url"
              className="family-links-modal-input"
              type="text"
              value={url}
              onChange={(e) => { setUrl(e.target.value); setError(''); }}
              placeholder="https://example.com"
            />
          </div>
          <div className="family-links-modal-field">
            <label className="family-links-modal-label" htmlFor="fl-cat">Category</label>
            <select
              id="fl-cat"
              className="family-links-modal-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {error && <div className="family-links-modal-error">{error}</div>}
          <div className="family-links-modal-actions">
            <button type="button" className="family-links-modal-btn" onClick={onClose}>
              cancel
            </button>
            <button type="submit" className="family-links-modal-btn family-links-modal-btn-primary">
              {editing ? 'save' : 'add'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LinkModal;
